/**
 * Two-row table head for `DataViewTable`:
 *   - Optional group header row (renders only when `hasColumnGroups`).
 *   - Column label row with sort indicators.
 *
 * Both rows pin via `position: sticky`; the caller supplies their `top:`
 * values (computed by `computeStickyTops`). Frozen-left columns get
 * their `left:` offsets via `getFreezeHeaderSx`. The group header row
 * forwards its ref so the parent can measure it for the column header's
 * top offset.
 *
 * Variant styling (inspector vs platform) is owned here, driven from the
 * single `isInspector` prop, so the parent doesn't need to plumb four
 * sx fragments through.
 */
import React from 'react'
import {
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material'
import type { ExceptionColumnDef } from '../../../config/exceptions/types'
import type { ColumnDef } from '../../../pages/admin/PlatformReports/reports/BaseReportPage'
import { getColumnAlignment } from '../../../utils/tableFormatters'
import { poolwerxColors } from '../../../theme/colors'
import {
  TABLE_ROW_DIVIDER,
  GROUP_HEADER_BG_INSPECTOR,
  GROUP_HEADER_TEXT,
  GROUP_HEADER_BOTTOM_BORDER,
  COLUMN_HEADER_BG_INSPECTOR,
  COLUMN_HEADER_TEXT,
  HEADER_BOTTOM_BORDER,
  STICKY_HEADER_SHADOW,
  GROUP_SEPARATOR_BORDER,
  FREEZE_SHADOW,
  SORTED_HEADER_TEXT,
  SORT_ICON_COLOR,
} from '../../../theme/exceptionTableStyles'
import { Z_GROUP_HEADER, Z_COLUMN_HEADER } from './stickyOffsets'

/**
 * Smooth the sticky `top:` change on every `<th>` when
 * `--app-chrome-height` flips on scroll (chrome collapse in
 * `InspectorLayout`). Applied via a child selector on `<TableHead>` so
 * every sticky header cell inherits the transition without each one
 * having to opt in via its own sx fragment.
 */
const TABLE_HEAD_STICKY_TRANSITION_SX = {
  '& th': { transition: 'top 200ms ease' },
}

export interface ColumnGroupDef {
  id: string
  label?: string
  separatorAfter?: boolean
}

export interface FreezeMetaEntry {
  left: number
  width: number
  isLastFrozen: boolean
}

export interface TableHeaderProps {
  visibleColumns: ExceptionColumnDef[]
  columnGroups: ColumnGroupDef[] | undefined
  hasColumnGroups: boolean
  hasExpandable: boolean
  hasRowDetail: boolean
  rowDetailHeaderLabel: string
  isInspector: boolean
  /**
   * Sticky `top:` values for the two header rows (CSS length
   * expressions, e.g. `'calc(var(--app-chrome-height, 120px) + 44px)'`).
   */
  groupHeaderStickyTop: string
  columnHeaderStickyTop: string
  /** Frozen-left column metadata + style helper from the parent. */
  freezeMeta: Map<string, FreezeMetaEntry>
  getFreezeHeaderSx: (col: ExceptionColumnDef) => Record<string, any>
  /** Keys whose right border marks a column-group separator. */
  groupSeparatorKeys: Set<string>
  orderBy: string
  order: 'asc' | 'desc'
  onSort: (key: string) => void
  /**
   * When set (server-sort mode), only these keys render a
   * `TableSortLabel`; other columns show a plain label. Absent = every
   * column sortable (client mode, today's behavior).
   */
  sortableKeys?: Set<string>
}

/**
 * Build the variant-specific style fragments. Pure derivation from
 * `isInspector` + theme tokens - kept inside the component so the
 * parent doesn't need to know about it.
 */
function buildVariantStyles(isInspector: boolean) {
  const groupHeaderSx = isInspector
    ? {
        backgroundColor: GROUP_HEADER_BG_INSPECTOR,
        color: GROUP_HEADER_TEXT,
        fontWeight: 800,
        textTransform: 'uppercase' as const,
        fontSize: '11px',
        letterSpacing: '0.06em',
        // `height` acts as a minimum on table cells: single-line labels stay
        // at 32px (vertically centred), but a label that wraps to two lines
        // grows the row. The roomier `py` gives wrapped labels (e.g. "Current
        // Stock / Avg Cost by Outlet") breathing room instead of sitting flush
        // against the borders. Measured height flows into the sticky stack.
        height: 32,
        py: 0.75,
        borderBottom: GROUP_HEADER_BOTTOM_BORDER,
        lineHeight: 1.2,
        verticalAlign: 'middle' as const,
      }
    : {
        backgroundColor: poolwerxColors.teal,
        color: '#FFFFFF',
        fontWeight: 500,
        textTransform: 'uppercase' as const,
        fontSize: '0.8rem',
        letterSpacing: '0.4px',
        py: 0.375,
        borderBottom: '1px solid rgba(255,255,255,0.18)',
        lineHeight: 1.2,
        verticalAlign: 'middle' as const,
      }

  const headerSx = isInspector
    ? {
        backgroundColor: COLUMN_HEADER_BG_INSPECTOR,
        borderBottom: HEADER_BOTTOM_BORDER,
      }
    : {
        backgroundColor: poolwerxColors.teal,
        borderBottom: `1px solid ${poolwerxColors.tealHover}`,
        color: 'white',
      }

  const headerCellSx = isInspector
    ? {
        fontWeight: 700,
        fontSize: '13px',
        color: COLUMN_HEADER_TEXT,
        height: 48,
        whiteSpace: 'pre-line' as const,
        lineHeight: 1.2,
        verticalAlign: 'middle' as const,
      }
    : {
        fontWeight: 600,
        color: 'white',
        textTransform: 'uppercase' as const,
        fontSize: '0.75rem',
        letterSpacing: '0.5px',
        whiteSpace: 'pre-line' as const,
        lineHeight: 1.2,
        verticalAlign: 'middle' as const,
      }

  const sortLabelSx: Record<string, any> = isInspector
    ? {
        '& .MuiTableSortLabel-icon': { color: SORT_ICON_COLOR },
        '&.Mui-active': {
          color: SORTED_HEADER_TEXT,
          fontWeight: 800,
          '& .MuiTableSortLabel-icon': { color: SORT_ICON_COLOR },
        },
      }
    : {
        color: 'white !important',
        '&.Mui-active': { color: 'white !important' },
        '& .MuiTableSortLabel-icon': { color: 'white !important' },
      }

  return { groupHeaderSx, headerSx, headerCellSx, sortLabelSx }
}

const TableHeader = React.forwardRef<HTMLTableRowElement, TableHeaderProps>(
  function TableHeader(
    {
      visibleColumns,
      columnGroups,
      hasColumnGroups,
      hasExpandable,
      hasRowDetail,
      rowDetailHeaderLabel,
      isInspector,
      groupHeaderStickyTop,
      columnHeaderStickyTop,
      freezeMeta,
      getFreezeHeaderSx,
      groupSeparatorKeys,
      orderBy,
      order,
      onSort,
      sortableKeys,
    },
    groupHeaderRef,
  ) {
    const { groupHeaderSx, headerSx, headerCellSx, sortLabelSx } =
      buildVariantStyles(isInspector)
    // Shadow on the bottommost sticky row (column header) when in
    // inspector mode. Applied uniformly across regular, expand, and
    // rowDetail cells so the shadow reads as one continuous line - an
    // earlier `&& hasColumnGroups` gate on the aux cells produced a
    // broken shadow for inspector tables with expand/compare but no
    // column groups.
    const columnHeaderShadowSx = isInspector
      ? { boxShadow: STICKY_HEADER_SHADOW }
      : {}

    return (
      <TableHead sx={TABLE_HEAD_STICKY_TRANSITION_SX}>
        {hasColumnGroups && columnGroups && (
          <TableRow ref={groupHeaderRef}>
            {hasExpandable && (
              <TableCell
                sx={{
                  ...groupHeaderSx,
                  width: 50,
                  padding: '4px 8px',
                  position: 'sticky',
                  top: groupHeaderStickyTop,
                  zIndex: Z_GROUP_HEADER,
                }}
              />
            )}
            {(() => {
              // Render one group-header cell per declared columnGroup,
              // PLUS one trailing cell covering any ungrouped columns at
              // the end (the right-frozen __notes__ / __actions__
              // affordances that the table auto-injects). Without the
              // trailing cell the group-header bar stops short of the
              // right edge and the bg colour goes missing above the
              // frozen affordances - visually fragmented on
              // columnGroups tables like product-setup / stock-management.
              const renderedGroupIds = new Set<string>()
              return columnGroups.map((group) => {
                renderedGroupIds.add(group.id)
                const groupCols = visibleColumns.filter(
                  (c) => c.group === group.id,
                )
                if (groupCols.length === 0) return null
                const anyFrozen = groupCols.some((c) => c.freeze === 'left')
                const firstCol = groupCols[0]
                const firstMeta = anyFrozen ? freezeMeta.get(firstCol.key) : undefined
                const isLastGroupFrozen =
                  anyFrozen && groupCols.every((c) => c.freeze === 'left')
                // Inspector's lighter teal band needs a darker group
                // separator than the white-on-teal block; reuse the row
                // divider so it matches the panel mood.
                const separatorBorder = isInspector
                  ? `1px solid ${TABLE_ROW_DIVIDER}`
                  : '1px solid rgba(255, 255, 255, 0.35)'
                return (
                  <TableCell
                    key={`group-${group.id}`}
                    colSpan={groupCols.length}
                    align="center"
                    sx={{
                      ...groupHeaderSx,
                      position: 'sticky',
                      top: groupHeaderStickyTop,
                      zIndex: Z_GROUP_HEADER,
                      ...(group.separatorAfter
                        ? { borderRight: separatorBorder }
                        : {}),
                      ...(firstMeta
                        ? {
                            left: firstMeta.left,
                            // Left-frozen group cells layer above scrolled
                            // body cells while still sitting under the
                            // toolbar.
                            zIndex: Z_GROUP_HEADER + 1,
                            ...(isLastGroupFrozen
                              ? { boxShadow: FREEZE_SHADOW }
                              : {}),
                          }
                        : {}),
                    }}
                  >
                    {group.label || ''}
                  </TableCell>
                )
              })
            })()}
            {/* Trailing group-header cell that covers any visible
                columns NOT mentioned in the columnGroups array - i.e.
                the auto-injected right-frozen affordances
                (__notes__ / __actions__). Without this cell the
                group-header band stops short of the right edge and
                leaves the bg-less gap the user flagged on
                negative-stock-business (and any other columnGroups
                table). Label is empty - these affordance columns
                don't need a heading, just the band continuity. */}
            {(() => {
              const groupedKeys = new Set<string>()
              for (const g of columnGroups) {
                for (const c of visibleColumns) {
                  if (c.group === g.id) groupedKeys.add(c.key)
                }
              }
              const trailingUngrouped = visibleColumns.filter(
                (c) => !groupedKeys.has(c.key),
              )
              if (trailingUngrouped.length === 0) return null
              return (
                <TableCell
                  key="group-trailing"
                  colSpan={trailingUngrouped.length}
                  sx={{
                    ...groupHeaderSx,
                    position: 'sticky',
                    top: groupHeaderStickyTop,
                    zIndex: Z_GROUP_HEADER,
                  }}
                />
              )
            })()}
            {hasRowDetail && (
              <TableCell
                align="center"
                sx={{
                  ...groupHeaderSx,
                  width: 100,
                  padding: '4px 8px',
                  position: 'sticky',
                  top: groupHeaderStickyTop,
                  zIndex: Z_GROUP_HEADER,
                }}
              >
                {rowDetailHeaderLabel}
              </TableCell>
            )}
          </TableRow>
        )}
        <TableRow>
          {hasExpandable && (
            <TableCell
              sx={{
                ...headerSx,
                ...headerCellSx,
                width: 50,
                padding: '8px',
                position: 'sticky',
                top: columnHeaderStickyTop,
                zIndex: Z_COLUMN_HEADER,
                ...columnHeaderShadowSx,
              }}
            />
          )}
          {visibleColumns.map((col) => {
            const colAlign =
              col.headerAlign ?? getColumnAlignment(col as ColumnDef)
            const sortLabelColSx: Record<string, any> =
              colAlign === 'right'
                ? {
                    // Right-aligned columns flip the sort arrow to the
                    // left of the label so the number reads to the
                    // cell's right edge.
                    ...sortLabelSx,
                    flexDirection: 'row-reverse',
                    '& .MuiTableSortLabel-icon': {
                      ...sortLabelSx['& .MuiTableSortLabel-icon'],
                      ml: 0,
                      mr: 0.5,
                    },
                  }
                : sortLabelSx
            return (
              <TableCell
                key={col.key}
                sx={{
                  ...headerSx,
                  ...headerCellSx,
                  textAlign: colAlign,
                  ...(col.minWidth ? { minWidth: col.minWidth } : {}),
                  ...(col.width ? { width: col.width } : {}),
                  ...((col as any).maxWidth ? { maxWidth: (col as any).maxWidth } : {}),
                  ...(!isInspector && col.key === 'last_sync_at'
                    ? { borderRight: '2px solid rgba(255,255,255,0.35)' }
                    : {}),
                  ...(groupSeparatorKeys.has(col.key)
                    ? { borderRight: GROUP_SEPARATOR_BORDER }
                    : {}),
                  position: 'sticky',
                  top: columnHeaderStickyTop,
                  zIndex: Z_COLUMN_HEADER,
                  ...columnHeaderShadowSx,
                  ...getFreezeHeaderSx(col),
                }}
                sortDirection={
                  col.renderHeader ? false : orderBy === col.key ? order : false
                }
              >
                {col.renderHeader ? (
                  col.renderHeader()
                ) : sortableKeys && !sortableKeys.has(col.key) ? (
                  col.label
                ) : (
                  <TableSortLabel
                    active={orderBy === col.key}
                    direction={orderBy === col.key ? order : 'asc'}
                    onClick={() => onSort(col.key)}
                    sx={sortLabelColSx}
                  >
                    {col.label}
                  </TableSortLabel>
                )}
              </TableCell>
            )
          })}
          {hasRowDetail && (
            <TableCell
              sx={{
                ...headerSx,
                ...headerCellSx,
                width: 100,
                padding: '8px',
                textAlign: 'center',
                position: 'sticky',
                top: columnHeaderStickyTop,
                zIndex: Z_COLUMN_HEADER,
                ...columnHeaderShadowSx,
              }}
            >
              {''}
            </TableCell>
          )}
        </TableRow>
      </TableHead>
    )
  },
)

export default TableHeader
